// frontend/modules/loans/stores/loan-application-store.ts
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {AppapproDTO, ApproverSelectionDTO, ProductDTO} from '@/shared/api/model';

// Define a structure for attachment data
interface AttachmentData {
    role: string;
    id: string;
    label?: string;
}

interface LoanApplicationState {
    currentStep: number;
    selectedProduct: ProductDTO | null;
    loanAmount: string | undefined;
    loanPeriod: string | undefined;
    grossSalary: string | undefined;
    netSalary: string | undefined;
    signedFormUploaded: boolean;
    signedFormId: string | undefined;
    selectedApprovers: ApproverSelectionDTO[];
    attachments: AttachmentData[]; // Changed to array of objects with proper structure
    temporaryAppapproDTO: AppapproDTO | null;
    loanBalanceData: any | null; // For storing product constraint data
    draftId: string | null; // For storing the current draft ID
}

interface LoanApplicationActions {
    setCurrentStep: (step: number) => void;
    setSelectedProduct: (product: ProductDTO | null) => void;
    setLoanAmount: (amount: string | undefined) => void;
    setLoanPeriod: (period: string | undefined) => void;
    setGrossSalary: (salary: string | undefined) => void;
    setNetSalary: (salary: string | undefined) => void;
    setSignedFormUploaded: (uploaded: boolean, fileId?: string) => void;
    setSelectedApprovers: (approvers: ApproverSelectionDTO[]) => void;
    setAttachment: (role: string, fileId: string, label?: string) => void;
    getAttachments: () => AttachmentData[];
    setTemporaryAppapproDTO: (dto: AppapproDTO | null) => void;
    setLoanBalanceData: (data: any | null) => void; // Function to set loan balance data
    setDraftId: (id: string | null) => void; // Function to set draft ID
    reset: () => void;
}

type LoanApplicationStore = LoanApplicationState & LoanApplicationActions;

const initialState: LoanApplicationState = {
    currentStep: 0,
    selectedProduct: null,
    loanAmount: undefined,
    loanPeriod: undefined,
    grossSalary: undefined,
    netSalary: undefined,
    signedFormUploaded: false,
    signedFormId: undefined,
    selectedApprovers: [],
    attachments: [], // Changed to empty array
    temporaryAppapproDTO: null,
    loanBalanceData: null,
    draftId: null
};

export const useLoanApplicationStore = create<LoanApplicationStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            setCurrentStep: (step) =>
                set({ currentStep: step }),

            setSelectedProduct: (product) =>
                set({ selectedProduct: product }),

            setLoanAmount: (amount) =>
                set({ loanAmount: amount }),

            setLoanPeriod: (period) =>
                set({ loanPeriod: period }),

            setGrossSalary: (salary) =>
                set({ grossSalary: salary }),

            setNetSalary: (salary) =>
                set({ netSalary: salary }),

            setSignedFormUploaded: (uploaded, fileId) => {
                console.log(`Setting signedFormUploaded to ${uploaded}, fileId: ${fileId}`);

                // Don't set as uploaded unless we have a valid fileId
                if (uploaded && fileId) {
                    set({
                        signedFormUploaded: true,
                        signedFormId: fileId
                    });

                    // Also add to attachments array for consistency
                    const state = get();
                    if (!state.attachments.some(a => a.role === "signed_application")) {
                        set({
                            attachments: [
                                ...state.attachments,
                                {
                                    role: "signed_application",
                                    id: fileId,
                                    label: "Signed Application Form"
                                }
                            ]
                        });
                    }

                    console.log(`Updated state: signedFormUploaded=true, signedFormId=${fileId}`);
                } else if (!uploaded) {
                    // Reset when explicitly setting to false
                    set(state => ({
                        signedFormUploaded: false,
                        signedFormId: undefined,
                        // Remove from attachments array too
                        attachments: state.attachments.filter(a => a.role !== "signed_application")
                    }));
                    console.log('Reset signed form state');
                }
            },

            setSelectedApprovers: (approvers) =>
                set({ selectedApprovers: approvers }),

            setAttachment: (role, fileId, label) => {
                if (!role || !fileId) {
                    console.error('Invalid parameters for setAttachment:', { role, fileId });
                    return;
                }

                console.log(`Setting attachment: ${role} -> ${fileId}`);

                set((state) => {
                    // Remove any existing attachment with the same role
                    const filteredAttachments = state.attachments.filter(a => a.role !== role);

                    // Add the new attachment
                    return {
                        attachments: [
                            ...filteredAttachments,
                            {
                                role,
                                id: fileId,
                                label: label || role
                            }
                        ]
                    };
                });

                console.log(`Current attachments:`, get().attachments);
            },

            getAttachments: () => {
                return get().attachments;
            },

            setTemporaryAppapproDTO: (dto) => {
                set({ temporaryAppapproDTO: dto });
            },
            
            setLoanBalanceData: (data) => {
                set({ loanBalanceData: data });
            },

            setDraftId: (id) => {
                set({ draftId: id });
            },

            reset: () => {
                console.log('Resetting loan application state');
                set(initialState);
            },
        }),
        { name: 'LoanApplicationStore' }
    )
);